<template>
  <button
    :class="[
      'prevent-disabled-hover cursor-pointer disabled:cursor-not-allowed',
      variantClasses,
      customClass,
      variantClass,
      { 'prevent-disabled-hover': enableHover },
    ]"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <div
      class="flex items-center justify-center gap-2"
      :class="{
        'flex-row-reverse': iconPos === 'right',
        'flex-row': iconPos === 'left',
      }"
    >
      <template v-if="!isLoading">
        <font-awesome-icon
          v-if="icon && iconType === 'icon'"
          :icon="icon"
          class="font-black"
          :class="iconComputedClass"
        />

        <img v-if="icon && iconType === 'image'" :src="icon" />
      </template>
      <template v-else>
        <span
          class="loader dark:text-surface-secondary text-surface-primary"
        ></span>
      </template>

      {{ label }}
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from "vue";

/* ========================
   📌 Types
========================= */
type IconType = "icon" | "image";
type IconPos = "left" | "right";
type Variant =
  | "primary"
  | "primary_outlined"
  | "success"
  | "success_outlined"
  | "warning_outlined"
  | "upload"
  | "link"
  | "text_blue"
  | "blue"
  | "danger"
  | "outlined"
  | "icon";

/* ========================
   📌 Props con PropType
========================= */
const props = defineProps({
  label: { type: String, default: "" },
  icon: { type: String, default: "" },

  iconType: {
    type: String as PropType<IconType>,
    default: "icon",
  },

  iconPos: {
    type: String as PropType<IconPos>,
    default: "left",
  },

  iconClass: { type: String, default: "" },
  customClass: { type: String, default: "" },

  disabled: { type: Boolean, default: false },
  enableHover: { type: Boolean, default: false },

  variant: {
    type: String as PropType<Variant>,
    default: "primary",
  },

  /**Loading Props */
  loading: { type: Boolean, default: false },
  loadingOnIcon: { type: Boolean, default: true },
  loadingPos: {
    type: String as PropType<IconPos>,
    default: "left",
  },
  loadingSelf: { type: Boolean, default: false },
  loadingSelfTimeout: { type: Number, default: 2000 },
});

const isDisabled = computed(() => props.disabled || isLoading.value);

const innerLoading = ref(false);

const isLoading = computed(() => {
  if (props.loadingSelf) {
    return innerLoading.value;
  }
  return props.loading;
});

const emit = defineEmits(["click"]);

const handleClick = (e: MouseEvent) => {
  if (props.loadingSelf) {
    if (isLoading.value) return; // evita spam

    innerLoading.value = true;

    setTimeout(() => {
      innerLoading.value = false;
    }, props.loadingSelfTimeout);
  }

  emit("click", e);
};

/* ========================
   🎨 Variant Classes
========================= */
const variantClasses = computed(() => {
  const base =
    "h-10 rounded font-bold flex items-center justify-center text-xs group";

  const variants: Record<Variant, string> = {
    success:
      "px-4 py-2 text-white bg-green-700 border border-green-700 " +
      "enabled:hover:bg-green-500 enabled:hover:border-green-500 " +
      "focus:bg-green-600 focus:border-green-700 " +
      "focus:outline-none " + // <- Cambiar esta línea
      "active:bg-green-700 active:border-green-700 " +
      "disabled:opacity-50",

    success_outlined:
      `px-4 py-2 border ${!props.disabled ? "border-success-green" : ""} ` +
      "enabled:text-green-800 enabled:hover:bg-success-green enabled:hover:text-white enabled:hover:border-success-green " +
      "focus:bg-success-green focus:text-white focus:border-success-green focus:outline-none " +
      "focus-visible:outline-none " +
      "active:bg-primary-active active:border-primary-active active:text-white " +
      "disabled:border-border-default disabled:text-dark-border disabled:bg-dark-secondary " +
      "dark:text-white disabled:dark:text-dark-border",

    warning_outlined:
      `px-4 py-2 border ${!props.disabled ? "!border-icon-warning" : ""} text-icon-warning ` +
      "enabled:hover:bg-icon-warning enabled:hover:text-white enabled:hover:border-icon-warning " +
      "focus:bg-icon-warning focus:text-white focus:border-icon-warning focus:outline-none " +
      "focus-visible:outline-none " +
      "active:bg-primary-active active:border-primary-active active:text-white " +
      "disabled:border-border-default disabled:text-dark-border disabled:bg-dark-secondary " +
      "dark:text-white disabled:dark:text-dark-border",

    blue:
      "px-4 py-2 text-white bg-blue-600 border border-blue-600 " +
      "enabled:hover:bg-blue-500 enabled:hover:border-blue-500 " +
      "focus:bg-blue-600 active:bg-blue-700 disabled:opacity-50",

    primary:
      "px-4 py-2 text-white bg-icon-tertiary border border-icon-tertiary " +
      "enabled:hover:bg-text-primary-hover enabled:hover:border-text-primary-hover " +
      "focus:bg-icon-tertiary active:bg-primary-active disabled:opacity-50",

    primary_outlined:
      "px-4 py-2 border border-icon-tertiary text-text-primary " +
      "enabled:hover:text-white enabled:hover:bg-text-primary-hover " +
      "focus:bg-icon-tertiary active:bg-primary-active active:text-white " +
      "disabled:text-dark-border disabled:border-border-default dark:text-white",

    text_blue:
      "px-4 py-2 text-accent-sky-60 enabled:hover:bg-surface-tertiary " +
      "active:text-link-active disabled:text-dark-border",

    link:
      "underline px-1 h-8 text-accent-sky-60 " +
      "enabled:hover:text-link-hover active:text-link-active disabled:text-dark-border",

    upload:
      "px-4 py-2 border border-accent-sky-60 text-accent-sky-60 " +
      "enabled:hover:bg-surface-tertiary",

    danger: "px-4 py-2 bg-red-600 text-white border border-red-600",

    outlined:
      "px-4 py-2 border border-border-default text-text-primary " +
      "enabled:hover:bg-surface-tertiary focus:bg-surface-tertiary " +
      "active:bg-primary-active disabled:text-dark-border disabled:border-border-default dark:text-white",

    icon: "px-4 py-2 text-icon-tertiary bg:white",
  };

  return `${base} ${variants[props.variant]}`;
});

/* ========================
   🎯 Icon class
========================= */
const iconComputedClass = computed(() => ({
  [props.iconClass]: !!props.iconClass,

  "text-white group-disabled:text-dark-border": props.variant === "primary",

  "text-icon-tertiary group-hover:text-white group-focus:text-white":
    props.variant === "primary_outlined",

  "group-disabled:text-dark-border": props.variant === "primary_outlined",

  "group-text-success-green group-hover:text-white group-focus:text-success-green":
    props.variant === "success_outlined" && !props.disabled,

  "text-disabled-default":
    ["success_outlined", "warning_outlined"].includes(props.variant) &&
    props.disabled,

  "text-icon-warning group-hover:text-white group-focus:text-icon-warning":
    props.variant === "warning_outlined" && !props.disabled,
}));

/* ========================
   🔠 Variant class name
========================= */
const variantClass = computed(() => props.variant.replace("_", "-"));
</script>

<style scoped>
button:disabled {
  cursor: not-allowed;
}

.prevent-disabled-hover:disabled:hover {
  background-color: #d7dde2 !important;
  border-color: #aab9c2 !important;
  color: #7a92a1 !important;
}

.loader {
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 0.25em solid currentColor;
  animation:
    l20-1 0.8s infinite linear alternate,
    l20-2 1.6s infinite linear;
}
@keyframes l20-1 {
  0% {
    clip-path: polygon(50% 50%, 0 0, 50% 0%, 50% 0%, 50% 0%, 50% 0%, 50% 0%);
  }
  12.5% {
    clip-path: polygon(
      50% 50%,
      0 0,
      50% 0%,
      100% 0%,
      100% 0%,
      100% 0%,
      100% 0%
    );
  }
  25% {
    clip-path: polygon(
      50% 50%,
      0 0,
      50% 0%,
      100% 0%,
      100% 100%,
      100% 100%,
      100% 100%
    );
  }
  50% {
    clip-path: polygon(
      50% 50%,
      0 0,
      50% 0%,
      100% 0%,
      100% 100%,
      50% 100%,
      0% 100%
    );
  }
  62.5% {
    clip-path: polygon(
      50% 50%,
      100% 0,
      100% 0%,
      100% 0%,
      100% 100%,
      50% 100%,
      0% 100%
    );
  }
  75% {
    clip-path: polygon(
      50% 50%,
      100% 100%,
      100% 100%,
      100% 100%,
      100% 100%,
      50% 100%,
      0% 100%
    );
  }
  100% {
    clip-path: polygon(
      50% 50%,
      50% 100%,
      50% 100%,
      50% 100%,
      50% 100%,
      50% 100%,
      0% 100%
    );
  }
}
@keyframes l20-2 {
  0% {
    transform: scaleY(1) rotate(0deg);
  }
  49.99% {
    transform: scaleY(1) rotate(135deg);
  }
  50% {
    transform: scaleY(-1) rotate(0deg);
  }
  100% {
    transform: scaleY(-1) rotate(-135deg);
  }
}
</style>
